import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import moment from 'moment';
import { NotificationHubCfgService } from './notificationHubCfg.service';

export enum NotificationChannel {
    Email = "Email",
    SMS = "SMS",
    Push = "Push"
}

export enum NotificationStatus {
    Enqueued = "Enqueued",
    Processed = "Processed",
    Rejected = "Rejected"
}

export enum AppType {
    Android = "Android",
    iOS = "iOS"
}

export enum DateOp {
    All = "All",
    Before = "Before",
    After = "After",
    Between = "Between"
}

export enum SMSMessageType {
    Transactional = "Transactional",
    Promotional = "Promotional"
}

export class NotificationSearchRequest {
    public recipientIdentity: RecipientIdentity = null;
    public applicationIdentity: ApplicationIdentity = null;
    public dateComp: DateOp = DateOp.All;
    public dateA: Date = null;
    public dateB: Date = null;
    public channel: NotificationChannel[] = [];
    public status: NotificationStatus[] = [];
    public itemsPerPage: number = 0;
    public paginationToken: string = null;
}

export class NotificationSearchResponse {
    traces: NotificationTrace[];
    paginationToken: string;
}

export class ApplicationIdentity {
    public business: string;
    public process: string;
    public event: string;
}

export class NotificationTrace {
    public id: string;
    public trackingTag: string;
    public channel: NotificationChannel
    public applicationIdentity: ApplicationIdentity;
    public status: NotificationStatus;
    public content: string;
    public recipient: string;
    public recipientIdentity: RecipientIdentity;
    public timestamp: number;
    public rejectionReason: string;
}

export abstract class RecipientIdentity {
    public readonly type: string;
    public writeParams(params: HttpParams): HttpParams {
        return params.append("recipientIdentity_type", this.type);
    }

    protected constructor(type: string) {
        this.type = type;
    }
}

export abstract class EmailBasedRecipientIdentity extends RecipientIdentity {
    constructor(type: string, email: string) {
        super(type);
        this.email = email;
    }

    public email: string;

    public writeParams(params: HttpParams): HttpParams {
        params = super.writeParams(params);
        return params.append("recipientIdentity_email", this.email);
    }
}

export class WorkerRecipientIdentity extends EmailBasedRecipientIdentity {
    constructor(email: string) {
        super("WorkerRecipientIdentity", email);
    }
}

export class ClientRecipientIdentity extends EmailBasedRecipientIdentity {
    constructor(email: string) {
        super("WorkerRecipientIdentity", email);
    }
}

export abstract class FilterConfigurationUpdateRequest {
    public readonly type: string;
    public readonly filter: string;

    protected constructor(type: string, filter: string) {
        this.type = type;
        this.filter = filter;
    }
}

export enum InputRecipientEntryOp {
    Add = "Add",
    Remove = "Remove",
    Update = "Update"
}

export class InputRecipientEntry {
    constructor(recipient: string, operation: InputRecipientEntryOp, type: string = "RecipientEntry") {
        this.type = type;
        this.recipient = recipient;
        this.operation = operation;
    }

    public readonly type: string;
    public recipient: string;
    public operation: InputRecipientEntryOp;
}

export abstract class RecipientFilterConfigurationUpdateRequest<TEntryType extends InputRecipientEntry> extends FilterConfigurationUpdateRequest {
    protected constructor(type: string, filter: string, recipientEntryUpdates: TEntryType[]) {
        super(type, filter);
        this.recipientEntryUpdates = recipientEntryUpdates;
    }

    public recipientEntryUpdates: TEntryType[];
}

export class RecipientBlackListFilterConfigurationUpdateRequest extends RecipientFilterConfigurationUpdateRequest<InputRecipientEntry> {
    constructor(recipientEntryUpdates: InputRecipientEntry[]) {
        super("RecipientBlackListFilterConfigurationUpdateRequest", "RecipientBlackListFilter", recipientEntryUpdates);
    }
}

export class RecipientWhiteListFilterConfigurationUpdateRequest extends RecipientFilterConfigurationUpdateRequest<InputRecipientEntry> {
    constructor(recipientEntryUpdates: InputRecipientEntry[]) {
        super("RecipientWhiteListFilterConfigurationUpdateRequest", "RecipientWhiteListFilter", recipientEntryUpdates);
    }
}

export abstract class FilterConfigurationQueryRequest {
    public readonly type: string;
    public readonly filter: string;

    protected constructor(type: string, filter: string) {
        this.type = type;
        this.filter = filter;
    }
}

export abstract class RecipientFilterSearchQueryRequest extends FilterConfigurationQueryRequest {
    protected constructor(type: string, filter: string, recipient: string, itemsPerPage: number, paginationToken: string) {
        super(type, filter);
        this.recipient = recipient;
        this.itemsPerPage = itemsPerPage;
        this.paginationToken = paginationToken;
    }
    public recipient: string;
    public itemsPerPage: number;
    public paginationToken: string;
}

export class RecipientBlackListFilterSearchQueryRequest extends RecipientFilterSearchQueryRequest {
    constructor(recipient: string, itemsPerPage: number = 0, paginationToken: string = null) {
        super("RecipientBlackListFilterSearchQueryRequest", "RecipientBlackListFilter", recipient, itemsPerPage, paginationToken);
    }
}

export class RecipientWhiteListFilterSearchQueryRequest extends RecipientFilterSearchQueryRequest {
    constructor(recipient: string, itemsPerPage: number = 0, paginationToken: string = null) {
        super("RecipientWhiteListFilterSearchQueryRequest", "RecipientWhiteListFilter", recipient, itemsPerPage, paginationToken);
    }
}

export abstract class FilterConfigurationQueryResponse {
    public readonly type: string;

    protected constructor(type: string) {
        this.type = type;
    }
}

export class OutputRecipientEntry {
    public readonly type: string;
    public recipient: string;
    public channel: NotificationChannel;

    constructor(type: string = "OuputRecipientEntry") {
        this.type = type;
    }
}

export abstract class RecipientFilterSearchQueryResponse<TRecipientEntry extends OutputRecipientEntry> extends FilterConfigurationQueryResponse {
    public recipients: TRecipientEntry[];
    public paginationToken: string;

    protected constructor(type: string) {
        super(type);
    }
}

export class RecipientBlackListFilterSearchQueryResponse extends RecipientFilterSearchQueryResponse<OutputRecipientEntry> {
    constructor() {
        super("RecipientBlackListFilterSearchQueryResponse");
    }
}

export class RecipientWhiteListFilterSearchQueryResponse extends RecipientFilterSearchQueryResponse<OutputRecipientEntry> {
    constructor() {
        super("RecipientWhiteListFilterSearchQueryResponse");
    }
}

export class GetApplicationIdentitiesRequest {
    public identity: ApplicationIdentity;
    public itemsPerPage: number;
    public paginationToken: string;
}

export class GetApplicationIdentitiesResponse {
    public identities: ApplicationIdentity[];
    public paginationToken: string;
}

export class CreateApplicationIdentityRequest {
    constructor(applicationIdentity: ApplicationIdentity) {
        this.identity = applicationIdentity;
    }

    public identity: ApplicationIdentity;
}

export class DeleteApplicationIdentityRequest {
    constructor(applicationIdentity: ApplicationIdentity) {
        this.identity = applicationIdentity;
    }

    public identity: ApplicationIdentity;
}

export class GetApplicationIdentityTreeResponse {
    public businesses: ApplicationIdentityBusiness[];
}

export class ApplicationIdentityBusiness {
    public name: string;
    public processes: ApplicationIdentityProcess[];
}

export class ApplicationIdentityProcess {
    public name: string;
    public events: ApplicationIdentityEvent[];
}

export class ApplicationIdentityEvent {
    public name: string;
}

export class MessageTemplateDefinition {
    constructor(content: string) {
        this.content = content;
    }

    public content: string;
}

export class Filter {
    constructor(name: string, parameters: { [id: string]: any }) {
        this.name = name;
        this.parameters = parameters;
    }

    public name: string;
    public parameters: { [id: string]: any };
}

export class DeliveryProvider {
    constructor(name: string, parameters: { [id: string]: any }) {
        this.name = name;
        this.parameters = parameters;
    }

    public name: string;
    public parameters: { [id: string]: any };

    public static ConfigureDefaultRejectProvider(): DeliveryProvider {
        return new DeliveryProvider("DefaultRejectProvider", {});
    }

    public static ConfigureNullProvider(): DeliveryProvider {
        return new DeliveryProvider("NullProvider", {});
    }

    public static ConfigureSESEmailProvider(): DeliveryProvider {
        return new DeliveryProvider("SESEmailProvider", {});
    }

    public static ConfigureSNSSMSProvider(messageType: SMSMessageType, senderId: string, maxPrice: number): DeliveryProvider {
        return new DeliveryProvider("SNSSMSProvider", { type: messageType, senderId: senderId, maxPrice: maxPrice });
    }

    public static ConfigureVodafoneRedboxSMSProvider(): DeliveryProvider {
        return new DeliveryProvider("VodafoneRedboxSMSProvider", {});
    }

    public static ConfigureMovistarSMSProvider(): DeliveryProvider {
        return new DeliveryProvider("MovistarSMSProvider", {});
    }

    public static ConfigureSitMobileSMSProvider(): DeliveryProvider {
        return new DeliveryProvider("SitMobileSMSProvider", {});
    }

    public static ConfigureSNSPushProvider(): DeliveryProvider {
        return new DeliveryProvider("SNSPushProvider", {});
    }
}

export class EmailAttachment {
    constructor(name: string, reference: string, mimeType: string) {
        this.name = name;
        this.reference = reference;
        this.mimeType = mimeType;
    }
    public name: string;
    public reference: string;
    public mimeType: string;
}

export abstract class SendNotificationData {
    public readonly channel: NotificationChannel;
    public trackingTag: string;
    public templateRef: string;
    public template: MessageTemplateDefinition;
    public parameters: { [id: string]: string };
    public recipient: string;
    public recipientIdentity: RecipientIdentity;
    public deliveryProvider: DeliveryProvider;

    constructor(channel: NotificationChannel) {
        this.channel = channel;
    }
}

export class SendEmailNotificationData extends SendNotificationData {
    public sender: string;
    public bbc: string[];
    public cc: string[];
    public subject: string;
    public subjectTemplate: MessageTemplateDefinition;
    public subjectTemplateRef: string;
    public fallbackTextTemplate: MessageTemplateDefinition;
    public fallbackTextTemplateRef: string;
    public attachments: EmailAttachment[];

    constructor() {
        super(NotificationChannel.Email);
    }
}

export class SendSMSNotificationData extends SendNotificationData {
    constructor() {
        super(NotificationChannel.SMS);
    }
}

export class SendPushNotificationData extends SendNotificationData {
    public appType: AppType;
    public extraData: any;

    constructor() {
        super(NotificationChannel.Push);
    }
}

export class SendNotificationRequest {
    public applicationIdentity: ApplicationIdentity;
    public globalTemplates: { [id: string]: MessageTemplateDefinition };
    public globalTemplateParameters: { [id: string]: string };
    public notifications: SendNotificationData[];
    public filters: Filter[];
}

export class SendNotificationDataOutput {
    public id: string;
    public trackingTag: string;
    public status: NotificationStatus;
    public rejectionReason: string;
}

export class SendNotificationResponse {
    public notifications: SendNotificationDataOutput[];
}

export class UploadAttachmentRequest {
    constructor(content: string = "") {
        this.content = content;
    }
    public content: string;
}

export class UploadAttachmentResponse {
    public reference: string;
}

@Injectable({
    providedIn: "root"
})
export class NotificationHubService {
    constructor(private cfg: NotificationHubCfgService, private httpClient: HttpClient) {

    }

    public async getByIdentity(request: NotificationSearchRequest): Promise<NotificationSearchResponse> {
        let endpoint = this.cfg.getCfg().getByIdentityEndpoint;

        let params = new HttpParams();

        if (request.recipientIdentity) {
            params = request.recipientIdentity.writeParams(params);
        }
        else {
            throw new Error("A recipient identity is mandatory to perform a notification search");
        }

        if (request.applicationIdentity) {
            if (request.applicationIdentity.business) params = params.append("applicationIdentity_business", request.applicationIdentity.business);
            if (request.applicationIdentity.process) params = params.append("applicationIdentity_process", request.applicationIdentity.process);
            if (request.applicationIdentity.event) params = params.append("applicationIdentity_event", request.applicationIdentity.event);
        }

        if (request.dateComp) {
            switch (request.dateComp) {
                case DateOp.Before:
                    {
                        if (request.dateA) {
                            params = params.append("dateComp", request.dateComp);
                            params = params.append("dateA", moment(request.dateA).toISOString());
                        }
                        else {

                        }
                        break;
                    }
                case DateOp.After:
                    {
                        if (request.dateA) {
                            params = params.append("dateComp", request.dateComp);
                            params = params.append("dateA", moment(request.dateA).toISOString());
                        }
                        else {

                        }
                        break;
                    }

                case DateOp.Between:
                    {
                        if (request.dateA && request.dateB) {
                            params = params.append("dateComp", request.dateComp);
                            params = params.append("dateA", moment(request.dateA).toISOString());
                            params = params.append("dateB", moment(request.dateB).toISOString());
                        }
                        else {

                        }
                        break;
                    }
            }
        }

        if (request.channel) {
            params = params.append("channel", request.channel.join(", "));
        }

        if (request.status) {
            params = params.append("status", request.status.join(", "));
        }

        if (request.itemsPerPage) {
            params = params.append("itemsPerPage", request.itemsPerPage.toString());
        }

        if (request.paginationToken) {
            params = params.append("paginationToken", request.paginationToken);
        }

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "");

        let response = await this.httpClient.get<NotificationSearchResponse>(endpoint, {
            headers: headers,
            params: params
        }).toPromise();
        return response;
    }

    public async updateFilterConfiguration(request: FilterConfigurationUpdateRequest): Promise<void> {
        let endpoint = this.cfg.getCfg().updateFilterConfigurationEndpoint;
        endpoint = endpoint.format(request.filter);

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "")
            .append("Content-Type", "application/json");
        await this.httpClient.put(endpoint, request, {
            headers: headers
        }).toPromise();
    }

    public async queryFilterConfiguration(request: FilterConfigurationQueryRequest): Promise<FilterConfigurationQueryResponse> {
        let endpoint = this.cfg.getCfg().queryFilterConfigurationEndpoint;
        endpoint = endpoint.format(request.filter);

        let params = new HttpParams();
        params = params.append("type", request.type);

        if (request instanceof RecipientBlackListFilterSearchQueryRequest) {
            let castedRequest = (request as RecipientBlackListFilterSearchQueryRequest);
            if (castedRequest.recipient) params = params.append("recipient", castedRequest.recipient);
            params = params.append("itemsPerPage", castedRequest.itemsPerPage.toString());
            if (castedRequest.paginationToken && castedRequest.paginationToken != "") params = params.append("paginationToken", request.paginationToken);
        }
        else if (request instanceof RecipientWhiteListFilterSearchQueryRequest) {
            let castedRequest = (request as RecipientWhiteListFilterSearchQueryRequest);
            if (castedRequest.recipient) params = params.append("recipient", castedRequest.recipient);
            params = params.append("itemsPerPage", castedRequest.itemsPerPage.toString());
            if (castedRequest.paginationToken && castedRequest.paginationToken != "") params = params.append("paginationToken", request.paginationToken);
        }

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "");

        let response = await this.httpClient.get<FilterConfigurationQueryResponse>(endpoint, {
            headers: headers,
            params: params
        }).toPromise();

        return response;
    }

    public async getApplicationIdentities(request: GetApplicationIdentitiesRequest): Promise<GetApplicationIdentitiesResponse> {
        let endpoint = this.cfg.getCfg().getApplicationIdentitiesEndpoint;
        let headers = new HttpHeaders()
            .append("AWSAuthorization", "");

        let params = new HttpParams();

        if (request.identity) {
            if (request.identity.business) params = params.append("applicationIdentity_business", request.identity.business);
            if (request.identity.process) params = params.append("applicationIdentity_process", request.identity.process);
            if (request.identity.event) params = params.append("applicationIdentity_event", request.identity.event);
        }

        if (request.itemsPerPage) {
            params = params.append("itemsPerPage", request.itemsPerPage.toString());
        }

        if (request.paginationToken) {
            params = params.append("paginationToken", request.paginationToken);
        }

        let response = await this.httpClient.get<GetApplicationIdentitiesResponse>(endpoint, {
            headers: headers,
            params: params
        }).toPromise();

        return response;
    }

    public async getApplicationIdentityTree(): Promise<GetApplicationIdentityTreeResponse> {
        let endpoint = this.cfg.getCfg().getApplicationIdentityTreeEndpoint;
        let headers = new HttpHeaders()
            .append("AWSAuthorization", "");
        let response = await this.httpClient.get<GetApplicationIdentityTreeResponse>(endpoint, {
            headers: headers
        }).toPromise();

        return response;
    }

    public async createApplicationIdentity(request: CreateApplicationIdentityRequest): Promise<void> {
        let endpoint = this.cfg.getCfg().createApplicationIdentityEndpoint;

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "")
            .append("Content-Type", "application/json");
        await this.httpClient.post(endpoint, request, {
            headers: headers
        }).toPromise();
    }

    public async deleteApplicationIdentity(request: DeleteApplicationIdentityRequest): Promise<void> {
        let endpoint = this.cfg.getCfg().deleteApplicationIdentityEndpoint;

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "")
            .append("Content-Type", "application/json");
        const options = {
            headers: headers,
            body: request
        };
        await this.httpClient.delete(endpoint, options).toPromise();
    }

    public async sendNotification(request: SendNotificationRequest): Promise<SendNotificationResponse> {
        let endpoint = this.cfg.getCfg().sendNotificationEndpoint;

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "")
            .append("Content-Type", "application/json");
        return await this.httpClient.post<SendNotificationResponse>(endpoint, request, {
            headers: headers
        }).toPromise();
    }

    public async uploadAttachment(request: UploadAttachmentRequest): Promise<UploadAttachmentResponse> {
        let endpoint = this.cfg.getCfg().uploadAttachmentEndpoint;

        let headers = new HttpHeaders()
            .append("AWSAuthorization", "")
            .append("Content-Type", "application/json");
        return await this.httpClient.post<UploadAttachmentResponse>(endpoint, request, {
            headers: headers
        }).toPromise();
    }
}
